import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { documentApi } from '../services/api';
import type { DocumentIntegrityDetails, DocumentRecord } from '../types';
import { signingProviderDisplayName } from '../utils/signingProvider';

interface DocumentIntegrityModalProps {
  document: DocumentRecord;
  onClose: () => void;
}

const formatBytes = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export function DocumentIntegrityModal({ document, onClose }: DocumentIntegrityModalProps) {
  const { t, i18n } = useTranslation();
  const [details, setDetails] = useState<DocumentIntegrityDetails | null>(null);
  const [error, setError] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let active = true;
    setDetails(null);
    setError(false);
    setCopied(false);

    documentApi.getDocumentDetails(document.id)
      .then(result => {
        if (active) setDetails(result);
      })
      .catch(() => {
        if (active) setError(true);
      });

    return () => {
      active = false;
    };
  }, [document.id]);

  const copyHash = async () => {
    if (!details?.sha256) return;
    try {
      await navigator.clipboard.writeText(details.sha256);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  const locale = i18n.language.startsWith('sv') ? 'sv-SE' : 'en-GB';

  return (
    <div
      className="modal d-block"
      style={{ backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 1070 }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="document-integrity-title"
      data-testid="document-integrity-modal"
      onClick={onClose}
    >
      <div className="modal-dialog modal-dialog-centered" onClick={event => event.stopPropagation()}>
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title" id="document-integrity-title">
              <i className="feather-shield me-2 text-primary"></i>
              {t('contracts.attachments.detailsTitle')}
            </h5>
            <button
              type="button"
              className="btn-close"
              onClick={onClose}
              aria-label={t('contracts.attachments.closeDetails')}
            ></button>
          </div>
          <div className="modal-body">
            <dl className="row mb-0">
              <dt className="col-sm-4">{t('contracts.attachments.fileName')}</dt>
              <dd className="col-sm-8 text-break">{document.originalName || document.fileName}</dd>

              <dt className="col-sm-4">{t('contracts.attachments.documentId')}</dt>
              <dd className="col-sm-8 text-break font-monospace">{document.id}</dd>

              <dt className="col-sm-4">{t('contracts.attachments.mimeType')}</dt>
              <dd className="col-sm-8">{document.mimeType}</dd>

              <dt className="col-sm-4">{t('contracts.attachments.fileSize')}</dt>
              <dd className="col-sm-8">{formatBytes(document.size)}</dd>

              <dt className="col-sm-4">{t('contracts.attachments.uploadedAt')}</dt>
              <dd className="col-sm-8">{new Date(document.createdAt).toLocaleString(locale)}</dd>
            </dl>

            <hr />

            <h6>{t('contracts.attachments.sha256')}</h6>
            {error ? (
              <div className="alert alert-danger mb-0" role="alert">
                {t('contracts.attachments.hashError')}
              </div>
            ) : !details ? (
              <div className="d-flex align-items-center gap-2 text-muted" role="status">
                <span className="spinner-border spinner-border-sm"></span>
                {t('contracts.attachments.hashLoading')}
              </div>
            ) : (
              <>
                <div className="d-flex align-items-start gap-2">
                  <code className="d-block flex-grow-1 border rounded bg-light p-2 text-break user-select-all">
                    {details.sha256}
                  </code>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-primary flex-shrink-0"
                    onClick={() => void copyHash()}
                  >
                    <i className={`feather-${copied ? 'check' : 'copy'} me-1`}></i>
                    {copied
                      ? t('contracts.attachments.hashCopied')
                      : t('contracts.attachments.copyHash')}
                  </button>
                </div>

                {details.integrityVerified ? (
                  <div className="text-success small mt-2">
                    <i className="feather-check-circle me-1"></i>
                    {t('contracts.attachments.hashVerified')}
                  </div>
                ) : (
                  <div className="alert alert-danger mt-3 mb-0" role="alert">
                    <div className="fw-semibold mb-1">
                      {t('contracts.attachments.hashMismatch')}
                    </div>
                    <div className="small">{t('contracts.attachments.expectedSha256')}</div>
                    <code className="d-block text-break user-select-all">{details.expectedSha256}</code>
                  </div>
                )}
                <p className="text-muted small mt-3 mb-0">
                  {t('contracts.attachments.hashHelp')}
                </p>

                <hr />
                <h6>{t('contracts.attachments.matchingRounds')}</h6>
                {(details.matchingSigningRounds ?? []).length === 0 ? (
                  <p className="text-muted small mb-0">
                    {t('contracts.attachments.noMatchingRounds')}
                  </p>
                ) : (
                  <ul className="list-group list-group-flush border rounded">
                    {(details.matchingSigningRounds ?? []).map(round => (
                      <li
                        className="list-group-item px-3 py-2"
                        key={round.signingRoundId}
                      >
                        <div className="fw-semibold">
                          {round.contractNumber == null
                            ? t('contracts.attachments.signingRound', {
                                round: round.roundNumber
                              })
                            : t('contracts.attachments.contractSigningRound', {
                                contract: round.contractNumber,
                                round: round.roundNumber
                              })}
                        </div>
                        <div className="text-muted small">
                          {signingProviderDisplayName(round.provider) || round.provider}
                          {' · '}
                          {new Date(round.createdAt).toLocaleString(locale)}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              {t('contracts.attachments.closeDetails')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
